import { IGetPreviousWizardStepDataOptions } from '../interfaces/helpers/IGetPreviousWizardStepDataOptions';
import { IPersistWizardStepDataOptions } from '../interfaces/helpers/IPersistWizardStepDataOptions';
import { IWizardContext, IWizardStepState } from '../interfaces/IWizard';
import { IWizardStepDataStrategyOptions } from '../interfaces/helpers/IWizardStepDataStrategyOptions';
import { IWizardStepDependencyMeta } from '../interfaces/helpers/IWizardStepDependencyMeta';
import { WizardStepDataCloneStrategy } from '../types/wizard-step-dependencies-types';

export function cloneWizardStepData<TData>(
  data?: TData | null,
  clone?: WizardStepDataCloneStrategy<TData>,
): TData | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  if (clone) {
    return clone(data);
  }

  return defaultCloneWizardStepData(data);
}

export function buildWizardStepDataSignature<TData>(
  data?: TData | null,
  options?: IWizardStepDataStrategyOptions<TData>,
): string {
  if (options?.signature) {
    return options.signature(data);
  }

  const snapshot = cloneWizardStepData(data, options?.clone);

  return JSON.stringify(snapshot) ?? 'undefined';
}

export function getWizardStepData<TData>(
  context: IWizardContext,
  stepId: number,
  options?: IWizardStepDataStrategyOptions<TData>,
): TData | undefined {
  return cloneWizardStepData(
    context.getStepState<TData>(stepId)?.data,
    options?.clone,
  );
}

export function getPreviousWizardStepData<TData>(
  context: IWizardContext,
  options?: IGetPreviousWizardStepDataOptions<TData>,
): TData | undefined {
  const currentStepId = options?.currentStepId ?? context.activeStepId();

  return getWizardStepData<TData>(context, currentStepId - 1, options);
}

export function buildWizardStepDependencyMeta<TData>(
  dependencyData?: TData | null,
  options?: IWizardStepDataStrategyOptions<TData>,
): IWizardStepDependencyMeta {
  return {
    dependencySignature: buildWizardStepDataSignature(dependencyData, options),
  };
}

export function shouldReuseWizardStepData<
  TData,
  TMeta extends IWizardStepDependencyMeta = IWizardStepDependencyMeta,
>(
  state: IWizardStepState<TData> | undefined,
  dependencyData?: TData | null,
  options?: IWizardStepDataStrategyOptions<TData>,
): boolean {
  const meta = state?.meta as TMeta | undefined;

  return (
    state?.data !== undefined &&
    meta?.dependencySignature ===
      buildWizardStepDataSignature(dependencyData, options)
  );
}

export function invalidateWizardSteps(
  context: IWizardContext,
  stepIds: readonly number[],
): void {
  for (const stepId of stepIds) {
    context.patchStepState(stepId, {
      dirty: false,
      valid: false,
      completed: false,
      data: undefined,
      meta: undefined,
      summary: undefined,
    });
  }
}

export function invalidateFollowingWizardSteps(
  context: IWizardContext,
  currentStepId: number,
  lastStepId: number,
): void {
  const stepIds: number[] = [];

  for (let stepId = currentStepId + 1; stepId <= lastStepId; stepId++) {
    stepIds.push(stepId);
  }

  invalidateWizardSteps(context, stepIds);
}

export function persistWizardStepData<TData, TMeta = unknown>(
  context: IWizardContext,
  options: IPersistWizardStepDataOptions<TData, TMeta>,
): TData | undefined {
  const stepId = options.stepId ?? context.activeStepId();
  const data = cloneWizardStepData(options.data, options.clone);
  const currentState = context.getStepState<TData>(stepId);
  const stepDataChanged =
    buildWizardStepDataSignature(currentState?.data, options) !==
    buildWizardStepDataSignature(data, options);

  if ((options.invalidateStepIds?.length ?? 0) > 0 && stepDataChanged) {
    invalidateWizardSteps(context, options.invalidateStepIds ?? []);
  }

  context.patchStepState(stepId, {
    dirty: options.dirty ?? true,
    valid: options.valid,
    visited: options.visited,
    completed: options.completed,
    data,
    meta: options.meta,
  });

  return data;
}

function defaultCloneWizardStepData<TData>(data: TData): TData {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(data);
  }

  if (Array.isArray(data)) {
    return data.map(item => defaultCloneArrayItem(item)) as TData;
  }

  if (isObjectRecord(data)) {
    return { ...data } as TData;
  }

  return data;
}

function defaultCloneArrayItem<TItem>(item: TItem): TItem {
  if (Array.isArray(item)) {
    return item.map(entry => defaultCloneArrayItem(entry)) as TItem;
  }

  if (isObjectRecord(item)) {
    return { ...item } as TItem;
  }

  return item;
}

function isObjectRecord(
  value: unknown,
): value is Record<string | number | symbol, unknown> {
  return typeof value === 'object' && value !== null;
}
